import { useMemo, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import * as Sentry from '@sentry/react';
import { Flex, Text, IconButton, Icon, Dropdown, Tooltip, EditableText, iconWeight, zIndex } from '@nula/core-ui';
import type { ConversationRow } from '@/lib/sync/entity-store';
import { updateConversation } from '../api/conversations';
import { relativeTime } from '../utils/relative-time';
import { ConversationBadge } from './conversation-badge';
import styles from './conversation-list-item.module.css';

interface ConversationListItemProps {
  row: ConversationRow;
  active: boolean;
  onSelect: (id: string) => void;
  onPin: (id: string, pinned: boolean) => void;
  onDelete: (id: string) => void;
}

const item = cva(styles.item);


export function ConversationListItem({
  row,
  active,
  onSelect,
  onPin,
  onDelete,
}: ConversationListItemProps) {
  const timeLabel = useMemo(() => relativeTime(row.latestActivityAt), [row.latestActivityAt]);
  const [editing, setEditing] = useState(false);

  const menuItems = useMemo(
    () => [
      {
        key: 'pin',
        label: row.isPinned ? 'Unpin' : 'Pin',
        icon: <Icon name="push_pin" size={14} weight={row.isPinned ? iconWeight.regular : iconWeight.light} />,
        onClick: () => onPin(row.id, !row.isPinned),
      },
      {
        key: 'rename',
        label: 'Rename',
        icon: <Icon name="edit" size={14} weight={iconWeight.light} />,
        onClick: () => setEditing(true),
      },
      { key: 'div', label: '', type: 'divider' as const },
      {
        key: 'delete',
        label: 'Delete',
        icon: <Icon name="delete" size={14} weight={iconWeight.light} />,
        danger: true,
        onClick: () => onDelete(row.id),
      },
    ],
    [row.id, row.isPinned, onPin, onDelete],
  );

  return (
    <Flex
      role="button"
      tabIndex={0}
      aria-label={row.title}
      aria-current={active ? 'page' : undefined}
      data-active={active ? 'true' : undefined}
      align="center"
      className={clsx(item(), active && styles.itemActive)}
      onClick={() => { if (!editing) onSelect(row.id); }}
      onKeyDown={(e) => {
        if (editing) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(row.id);
        }
      }}
    >
      {row.isPinned && (
        <Tooltip title="Pinned" placement="right">
          <Icon name="push_pin" size={12} weight={iconWeight.regular} className={styles.pinIcon} />
        </Tooltip>
      )}
      <Flex
        vertical
        className={styles.titleRow}
        // While editing, stop row-level click/keydown from firing select/navigation.
        onClick={editing ? (e) => e.stopPropagation() : undefined}
      >
        {editing ? (
          // EditableText drives the input, keyboard (Enter commit / Escape
          // cancel), and blur-commit. We only render it while editing — the
          // read-only title stays plain Text so it isn't a competing click
          // target (clicking the row opens the conversation; the menu renames).
          <EditableText
            value={row.title}
            size="sm"
            editing
            onEditingChange={setEditing}
            onCancel={() => setEditing(false)}
            onChange={(next) => {
              setEditing(false);
              const title = next.trim();
              if (!title || title === row.title) return;
              updateConversation(row.id, { title }).catch((err) => {
                Sentry.captureException(err, { extra: { action: 'rename', id: row.id } });
              });
            }}
            className={styles.title}
          />
        ) : (
          <Text
            size="sm"
            ellipsis
            className={styles.title}
            // Double-click the title to rename (in addition to the ⋯ menu's
            // Rename action). Matches the tab-bar's double-click-to-rename.
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
          >
            {row.title}
          </Text>
        )}
        <Text size="xs" className={styles.time}>{timeLabel}</Text>
      </Flex>
      {/* The badge is NOT suppressed by active=true — clearing is provider-side:
          loadConversation calls useAssistantNotificationsStore.clear(conversationId)
          when the user focuses the conversation, so the badge disappears at that
          point, not here in the list item. */}
      <ConversationBadge conversationId={row.id} />
      <Flex className={styles.actions} onClick={(e) => e.stopPropagation()}>
        <Dropdown
          trigger={['click']}
          menu={{ items: menuItems }}
          placement="bottom-end"
          // The list renders inside a right-side Drawer (z-index zIndex.modal+1).
          // Default dropdown z (200) would render the menu BEHIND the drawer
          // panel. Lift it to the popover layer so it stacks above the drawer.
          menuStyle={{ '--dropdown-z': zIndex.popover } as React.CSSProperties}
        >
          <IconButton size="sm" aria-label="Conversation actions">
            <Icon name="more_horiz" size={14} weight={iconWeight.light} />
          </IconButton>
        </Dropdown>
      </Flex>
    </Flex>
  );
}
